import type { UserRole } from '@autopia/shared-types';
import { z } from 'zod';

/**
 * Roles the admin console can assign to a staff account.
 *
 * `satisfies readonly UserRole[]` makes it a compile error to list a role that
 * does not exist, so this cannot drift into an independent vocabulary the way
 * `UserRole` itself did (#828).
 *
 * Kept as its own list rather than derived from `STAFF_ROLES` in
 * `@autopia/shared-types`: the two answer different questions ("who may be
 * given a staff account" vs "who may appear in a staff login response"), and
 * collapsing them would mean a future decision to stop offering a role in the
 * console silently removes it from login responses too. They happen to agree
 * on membership today, which is the #828 divergence being closed rather than a
 * reason to merge them.
 *
 * The ten gate roles (#958) are assignable — a gate account is created here
 * like any other. What such an account may then reach is `RolesGuard`'s
 * business, not this list's.
 */
export const ASSIGNABLE_STAFF_ROLES = [
  'STAFF',
  'SALES',
  'GATE_OPERATOR',
  'SUPERVISOR',
  'MANAGER',
  'ADMIN',
  'SUPER_ADMIN',
  'KIOSK',
  'MAIN_GATE_VALIDATOR_IN',
  'MAIN_GATE_VALIDATOR_OUT',
  'ATTRACTION_VALIDATOR_IN',
  'ATTRACTION_VALIDATOR_OUT',
  'ZONE_VALIDATOR_IN',
  'ZONE_VALIDATOR_OUT',
  'GATE_SUPERVISOR',
  'MAIN_GATE_SUPERVISOR',
  'ATTRACTION_GATE_SUPERVISOR',
  'OPERATIONS_SUPERVISOR',
] as const satisfies readonly UserRole[];

export const StaffRoleSchema = z.enum(ASSIGNABLE_STAFF_ROLES);
export type StaffRole = z.infer<typeof StaffRoleSchema>;

export const StaffApprovalStatusSchema = z.enum([
  'PENDING_APPROVAL',
  'APPROVED',
  'REJECTED',
]).nullable();
export type StaffApprovalStatus = z.infer<typeof StaffApprovalStatusSchema>;

export const StaffLocationTypeSchema = z.enum(['GATE', 'ATTRACTION', 'KIOSK_ZONE', 'OFFICE']);
export type StaffLocationType = z.infer<typeof StaffLocationTypeSchema>;

export const StaffLocationSchema = z.object({
  id: z.string(),
  locationType: StaffLocationTypeSchema,
  locationId: z.string(),
  locationName: z.string(),
  isPrimary: z.boolean(),
  assignedAt: z.string(),
});
export type StaffLocation = z.infer<typeof StaffLocationSchema>;

export const StaffUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  role: StaffRoleSchema,
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']),
  isActive: z.boolean(),
  isSuspended: z.boolean(),
  has2FA: z.boolean(),
  totpEnabled: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
  employeeId: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  position: z.string().nullable().optional(),
  approvalStatus: StaffApprovalStatusSchema.optional(),
  locations: z.array(StaffLocationSchema).optional(),
});
export type StaffUser = z.infer<typeof StaffUserSchema>;

export const StaffUsersResponseSchema = z.object({
  users: z.array(StaffUserSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type StaffUsersResponse = z.infer<typeof StaffUsersResponseSchema>;

export const StaffUsersQuerySchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  search: z.string().optional(),
  role: StaffRoleSchema.optional(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']).optional(),
});
export type StaffUsersQuery = z.infer<typeof StaffUsersQuerySchema>;

export const CreateStaffSchema = z.object({
  email: z.string().email(),
  name: z.string().min(2).max(100),
  phone: z.string().optional(),
  employeeId: z.string().min(1).max(30).regex(/^[A-Z0-9][A-Z0-9-]*[A-Z0-9]$|^[A-Z0-9]$/i).optional(),
  department: z.string().max(100).optional(),
  position: z.string().max(100).optional(),
  role: StaffRoleSchema,
  password: z.string().min(8).max(72).regex(/[A-Z]/).regex(/[0-9]/).regex(/[^A-Za-z0-9]/),
});
export type CreateStaffInput = z.infer<typeof CreateStaffSchema>;

export const UpdateStaffSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  role: StaffRoleSchema.optional(),
});
export type UpdateStaffInput = z.infer<typeof UpdateStaffSchema>;

export const AssignStaffLocationSchema = z.object({
  locationType: StaffLocationTypeSchema,
  locationId: z.string(),
  locationName: z.string(),
  isPrimary: z.boolean(),
});
export type AssignStaffLocationInput = z.infer<typeof AssignStaffLocationSchema>;

export const StaffLocationsResponseSchema = z.object({
  locations: z.array(StaffLocationSchema),
});
export type StaffLocationsResponse = z.infer<typeof StaffLocationsResponseSchema>;

export const StaffAuditEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  actor: z.string(),
  timestamp: z.string(),
  details: z.string().optional(),
});
export type StaffAuditEntry = z.infer<typeof StaffAuditEntrySchema>;

export const StaffAuditResponseSchema = z.object({
  entries: z.array(StaffAuditEntrySchema),
});
export type StaffAuditResponse = z.infer<typeof StaffAuditResponseSchema>;
